/**
 * PostHog Analytics for DIY Living Project Guides
 *
 * Tracks user engagement with customization features for conversion optimization.
 * Event names: see `POSTHOG_EVENTS` in `@/lib/posthog/events`.
 */

import type { PostHog } from 'posthog-js'
import { POSTHOG_EVENTS, safeCapture } from '@/lib/posthog'

/**
 * Track SEO template page load (top of funnel — anon + auth).
 * Fires once per client navigation to `/diy/:slug`.
 */
export function trackTemplatePageViewed(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    category?: string
    difficulty?: string
    referrer?: string
    utm_source?: string
    utm_medium?: string
    utm_campaign?: string
    utm_content?: string
    utm_term?: string
    /** Raw query keys present (e.g. URL preset flags); omit large values */
    searchParamKeys?: Array<string>
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_TEMPLATE_PAGE_VIEWED, properties)
}

/**
 * Track when user starts customizing their plan (first dropdown change)
 */
export function trackPlanCustomizationStarted(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    questionKey: string
    source: 'customize_sheet' | 'inline'
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_PLAN_CUSTOMIZATION_STARTED, properties)
}

/**
 * Track when user completes all required customization questions
 */
export function trackPlanCustomizationCompleted(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    answerCount: number
    timeSpentSeconds: number
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_PLAN_CUSTOMIZATION_COMPLETED, properties)
}

/**
 * Track when user opens AI refinement chat
 */
export function trackAiChatOpened(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    promptsRemaining?: number
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_AI_CHAT_OPENED, properties)
}

/**
 * Track when user clicks save CTA
 */
export function trackSaveCtaClicked(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    isCustomized: boolean
    source: 'toc' | 'fab' | 'inline' | 'conversion_section'
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_SAVE_CTA_CLICKED, properties)
}

/**
 * Track successful conversion (signup + project claim)
 */
export function trackConversionCompleted(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    personalizationPath: 'customized' | 'default'
    answerCount: number
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_CONVERSION_COMPLETED, properties)
}

/**
 * Track TOC navigation clicks
 */
export function trackTocNavigation(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    targetSection: string
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_TOC_NAVIGATION, properties)
}

/**
 * Track related project clicks
 */
export function trackRelatedProjectClicked(
  posthog: PostHog | undefined,
  properties: {
    sourceProjectSlug: string
    targetProjectSlug: string
    position: number
    toolOverlap: 'same_kit' | 'additional_tools'
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_RELATED_PROJECT_CLICKED, properties)
}

/**
 * Track project completion (100% steps done)
 */
export function trackProjectCompleted(
  posthog: PostHog | undefined,
  properties: {
    projectTitle: string
    totalSteps: number
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.PROJECT_COMPLETED, properties)
}

/**
 * Track when user clicks a maintenance nudge CTA
 */
export function trackMaintenanceNudgeClicked(
  posthog: PostHog | undefined,
  properties: {
    projectTitle: string
    source: 'completion_card' | 'mid_project'
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.MAINTENANCE_NUDGE_CLICKED, properties)
}

/**
 * Track when user clicks a feature in the dashboard Discovery Row
 */
export function trackDiscoveryFeatureClicked(
  posthog: PostHog | undefined,
  properties: {
    feature: 'track_home' | 'maintenance_reminders' | 'more_projects'
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DISCOVERY_FEATURE_CLICKED, properties)
}

/**
 * Track FAQ expansion
 */
export function trackFaqExpanded(
  posthog: PostHog | undefined,
  properties: {
    projectSlug: string
    questionIndex: number
    questionText: string
  },
) {
  safeCapture(posthog, POSTHOG_EVENTS.DIY_FAQ_EXPANDED, properties)
}
